import { FileText, Phone } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../components/auth/AuthRequiredRoute/AuthRequiredRoute";
import ConfirmSection from "../../components/common/ConfirmSection/ConfirmSection";
import LoadingPage from "../../components/LoadingPage/LoadingPage";
import InstallmentTable from "../../components/loan/InstallmentTable/InstallmentTable";
import Button from "../../components/ui/Button/Button";
import Dialog from "../../components/ui/Dialog/Dialog";
import Icon from "../../components/ui/Icon/Icon";
import OutlinedButton from "../../components/ui/OutlinedButton";
import Column from "../../components/ui/Stack/Column";
import HStack from "../../components/ui/Stack/HStack";
import { useLoanInfo } from "../../features/loans/hooks/useLoanInfo";
import { UpdateLoan } from "../../services/modules/loan";
import {
  getLatestInstallment,
  shouldShowAcceptAgreementDocumentButton,
  shouldShowAgreementDocumentInfoButton,
  shouldShowCancelLoanButton,
  shouldShowCheckNewLoanPrincipalButton,
  shouldShowContactButton,
  shouldShowCreateNewLoanButton,
  shouldShowEditLoanButton,
  shouldShowInstallmentTable,
  shouldShowLoanContactExample,
  shouldShowMakePaymentButton,
  shouldShowReceiptButton,
} from "../../utils/loan";
import { formatMoneyNumber } from "../../utils/util";
import SuccessSection from "../EditMyLoan/SuccessSection";
import HeaderComponent from "../Header/HeaderComponent";
import HeaderDesktop from "../Header/HeaderDesktop";
import Detail from "./_components/Detail/Detail";
import styles from "./style.module.css";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useTranslation } from "react-i18next";
import { allowMakePayment } from "../../utils/loan";

const MyLoanDetailPage = () => {
  const { loanId } = useParams();
  const { t } = useTranslation("myLoanItem");
  const { userData } = useAuth();

  const { loanInfo, isFetching: isFetchingLoanInfo } = useLoanInfo({
    loanId,
    userData,
  });

  const [isShowAcceptLoanSuccessSection, setIsShowAcceptLoanSuccessSection] =
    useState(false);

  const [isShowCancelLoanConfirmSection, setIsShowCancelLoanConfirmSection] =
    useState(false);

  const [
    isShowAcceptNewLoanPrincipalConfirmDialog,
    setIsShowAcceptNewLoanPrincipalConfirmDialog,
  ] = useState(false);

  const [isNewLoanPrincipalSuccess, setIsNewLoanPrincipalSuccess] =
    useState(false);

  const latestInstallment = getLatestInstallment(loanInfo);

  const isAllowMakePayment = useMemo(() => {
    return allowMakePayment(latestInstallment);
  }, [latestInstallment]);

  const navigate = useNavigate();

  const navigateBack = () => {
    navigate("/my-loans");
  };
  const isDesktop = useIsDesktop();

  async function handleShowLoanReceipt() {
    navigate(`/my-loans/${loanId}/receipt`);
  }

  async function handleShowLoanContract() {
    navigate(`/my-loan-contract/${loanId}`);
  }

  if (isFetchingLoanInfo) return <LoadingPage message={t("loading")} />;
  return (
    <div className={styles.container}>
      {isDesktop ? (
        <HeaderDesktop
          title={`${t("loan")} : ${loanInfo?.product_name}`}
          showIcon={true}
          onBack={true}
        />
      ) : (
        <HeaderComponent
          title={`${t("loan")} : ${loanInfo?.product_name}`}
          onBack={navigateBack}
        />
      )}
      {isShowAcceptLoanSuccessSection && (
        <SuccessSection
          icon={<Icon size={72} name="ico-document" />}
          onNext={async () => {
            window.location.reload();

            setIsShowAcceptLoanSuccessSection(false);
          }}
          title={t("title")}
          descriptionLine1={t("descriptionLine1")}
          descriptionLine2={t("descriptionLine2")}
        />
      )}

      {isShowCancelLoanConfirmSection && (
        <ConfirmSection
          icon={<Icon size={72} name="ico-document-cancel" />}
          title={t("title2")}
          onCancel={() => setIsShowCancelLoanConfirmSection((_) => false)}
          onConfirm={async () => {
            await UpdateLoan({
              body: { loan_id: loanId, loan_state: "cancel" },
            });

            window.location.reload();

            setIsShowCancelLoanConfirmSection((_) => false);
          }}
          descriptionLine1={t("descriptionLine1.2")}
          descriptionLine2={t("descriptionLine2.2")}
          confirmButtonText={t("confirmButtonText")}
          cancelButtonText={t("cancelButtonText")}
        />
      )}
      {isNewLoanPrincipalSuccess && (
        <SuccessSection
          icon={<Icon size={72} name="ico-email" />}
          title={t("title3")}
          descriptionLine1={t("descriptionLine1.3")}
          confirmButtonText={t("confirmButtonText2")}
          onNext={async () => {
            await UpdateLoan({
              body: { loan_id: loanId, loan_state: "accept" },
            });

            window.location.reload();

            setIsNewLoanPrincipalSuccess(false);
          }}
        />
      )}
      <Dialog
        isShow={isShowAcceptNewLoanPrincipalConfirmDialog}
        onClose={() => setIsShowAcceptNewLoanPrincipalConfirmDialog(false)}
        contentMode="built-in"
      >
        <div style={{ textAlign: "center", padding: 2, maxWidth: 320 }}>
          <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
            {t("acceptChange")}
          </div>
          <div style={{ color: "#667085", fontSize: 14, marginBottom: 16 }}>
            {t("newChange")}
          </div>
          <HStack
            style={{ width: 300, marginBottom: 10 }}
            justify="center"
            align="center"
            gap={8}
            fullWidth
          >
            <span
              style={{
                color: "#101828",
                textAlign: "center",
                width: "100%",
                fontWeight: 700,
                fontSize: 20,
              }}
            >
              {formatMoneyNumber(loanInfo?.loan_amount)}
            </span>
          </HStack>
          <div style={{ color: "#667085", fontSize: 13, marginBottom: 24 }}>
            {t("confirm")}
          </div>
          <Button
            variant="primary"
            fullWidth
            style={{ marginBottom: 12 }}
            onClick={() => {
              setIsShowAcceptNewLoanPrincipalConfirmDialog(false);
              setIsNewLoanPrincipalSuccess(true);
            }}
          >
            {t("accept")}
          </Button>
          <Button
            variant="default"
            fullWidth
            onClick={() => {
              setIsShowAcceptNewLoanPrincipalConfirmDialog(false);
            }}
          >
            {t("cancle")}
          </Button>
        </div>
      </Dialog>
      {!isShowAcceptLoanSuccessSection && !isShowCancelLoanConfirmSection && (
        <>
          {loanInfo && <Detail loanInfo={loanInfo} />}
          {loanInfo && loanInfo.loan_state === "open" && (
            <Column style={{ margin: 10, marginTop: 20 }} gap={4}>
              <Button size="lg" fullWidth onClick={handleShowLoanReceipt}>
                <span>{t("checkReceipt")}</span>
              </Button>
              <Button onClick={handleShowLoanContract} size="lg" fullWidth>
                <span>{t("contract")}</span>
              </Button>
            </Column>
          )}
          {shouldShowInstallmentTable(loanInfo) && (
            <>
              <h3 className={styles.tableTitle}>{t("tableInterest")}</h3>
              <InstallmentTable
                installments={loanInfo.installments}
                totalInstallmentAmount={loanInfo.loan_amount}
              />
            </>
          )}
          {shouldShowLoanContactExample(loanInfo) && (
            <embed
              className={styles.pdf}
              src="/assets/loan-contract-example.pdf"
            />
          )}
          {/* {shouldShowLoanDocumentList(loanInfo) && (
        <List
          style={{ padding: "20px" }}
          items={loanInfo.loan_documents}
          renderItem={(documentInfo) => (
            <LoanDocumentCard documentInfo={documentInfo} />
          )}
        />
      )} */}
          <footer className={styles.footer}>
            {shouldShowEditLoanButton(loanInfo) && (
              <Button
                variant="primary"
                onClick={() => navigate(`/my-loans/${loanId}/edit`)}
              >
                {t("editPaper")}
              </Button>
            )}
            {shouldShowMakePaymentButton(loanInfo, latestInstallment) && (
              <Button
                variant="primary"
                disabled={!isAllowMakePayment}
                onClick={() => navigate(`/my-loans/${loanId}/payment`)}
              >
                {t("pay")}
              </Button>
            )}

            {(loanInfo?.loan_state === "open" ||
              loanInfo?.loan_state === "close") && (
              <Button
                icon={<FileText size={20} />}
                onClick={() => navigate(`/my-loans/${loanId}/invoice`)}
              >
                {t("invoice")}
              </Button>
            )}

            {shouldShowReceiptButton(loanInfo) && (
              <Button
                icon={<FileText size={20} />}
                onClick={() => navigate(`/my-loans/${loanId}/receipt`)}
              >
                {t("checkReceipt")}
              </Button>
            )}
            {shouldShowCheckNewLoanPrincipalButton(loanInfo) && (
              <>
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsShowAcceptNewLoanPrincipalConfirmDialog(true);
                  }}
                >
                  {t("checkLoan")}
                </Button>
                <Button variant="secondary" onClick={() => {}}>
                  {t("cancelLoan")}
                </Button>
              </>
            )}
            {shouldShowCreateNewLoanButton(loanInfo) && (
              <Button
                variant="primary"
                onClick={() => navigate(`/select-loan/income`)}
              >
                {t("registerLoan")}
              </Button>
            )}
            {shouldShowAgreementDocumentInfoButton(loanInfo) && (
              <Button
                onClick={handleShowLoanContract}
                icon={<FileText size={20} />}
              >
                {t("contract")}
              </Button>
            )}
            {shouldShowContactButton(loanInfo) && (
              <Button icon={<Phone size={20} />}>{t("contactUs")}</Button>
            )}
            {shouldShowAcceptAgreementDocumentButton(loanInfo) && (
              <Button
                variant="primary"
                onClick={async () => {
                  await UpdateLoan({
                    body: { loan_id: loanId, loan_state: "accept" },
                  });

                  setIsShowAcceptLoanSuccessSection((_) => true);
                }}
              >
                {t("acceptContract")}
              </Button>
            )}
            {shouldShowCancelLoanButton(loanInfo) && (
              <OutlinedButton
                onClick={() => setIsShowCancelLoanConfirmSection((_) => true)}
              >
                {t("cancleContract")}
              </OutlinedButton>
            )}
          </footer>
        </>
      )}
    </div>
  );
};

export default MyLoanDetailPage;
